/* eslint-disable no-control-regex */
import { CFormTextarea } from '@coreui/react-pro'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  asteriskSearchLength,
  changeCursor,
  jumpToAsterisk,
  onDotPhraseSnippetAdd,
  setNoteSectionValue,
  turnOffJumpCode,
} from 'src/redux/progressNote/progressNoteSlice'
import DotPhrase from './DotPhrase'
import getCaretCoordinates from 'textarea-caret'
import _ from 'lodash'
import { TAB_OPTIONS } from './progressNoteConstants'
import { checkItsHaveOnlyTextArea } from '../../../helper/formater'
import FEATURES from '../../../utils/featuresConstants'

const KEYS_DOT_PHRASE_IGNORES = ['ArrowLeft', 'ArrowRight']
const ACTIONS = { TYPE: 'TYPE', FOCUS: 'FOCUS', BLUR: 'BLUR' }

const DynamicSection = ({
  section,
  activeSectionHandle,
  isTyping,
  setIsTyping,
  isDraftSaved,
  setIsDraftSaved,
  currentTab,
  setCurrentTab,
  snippets,
  onSaveDraft,
}) => {
  const dispatch = useDispatch()
  const [highlightedSnippet, setHighlightedSnippet] = useState(0)
  const [useDotPhrase, setUseDotPhrase] = useState(false)
  const [startingDot, setStartingDot] = useState(-1)
  const [dotCaret, setDotCaret] = useState(0)
  const [dotPhrasePosition, setDotPhrasePosition] = useState({ top: 0, left: 0 })

  const activeSection = useSelector((state) => state?.progressNote?.activeSection)
  const cursorIndex = useSelector((state) => state?.progressNote?.cursorIndex)
  const useJumpCode = useSelector((state) => state?.progressNote?.useJumpCode)
  const asterisksFound = useSelector((state) => state?.progressNote?.asterisksFound)
  const canUseDragon = useSelector((state) => state?.user?.settings?.canUseDragon)
  const features = useSelector((state) => state?.user?.features)
  const dragonMicOn = features?.includes(FEATURES.Dragon) && canUseDragon

  // this feels off
  const isHaveOnlyTextarea = activeSection ? checkItsHaveOnlyTextArea(activeSection) : false
  const noteTxt = section?.values
    ? Object.values(section?.values?.filter((item) => item !== undefined))?.join('')
    : ''

  const valueText = isTyping
    ? section?.values
      ? isHaveOnlyTextarea
        ? Object.values(section?.values?.filter((item) => item !== undefined))?.join('')
        : Object.values(section?.values?.filter((item) => item !== undefined))
            ?.join('')
            ?.replace(/[^\x00-\x7F]/g, ' ')
      : ''
    : noteTxt?.replace(/[^\x00-\x7F]/g, ' ')

  const isActiveSection = activeSection?.title === section?.title

  useEffect(() => {
    if (!isActiveSection) {
      // close dot phrasing for unfocused text-areas
      resetDotPhrasing()

      // if we are also not active, blur/remove focus
      refElement.current.blur()
    } else {
      if (refElement.current.classList.contains('active')) {
        refElement.current.focus()
      }
    }
  }, [useDotPhrase, isActiveSection])

  const refElement = useRef(null)
  const hiddenRef = useRef(null)
  useEffect(() => {
    adjustTextAreaHeight()

    // ensure the text area cursor is where we expect it to be, not at end or beginning, but where text is added
    if (refElement.current.selectionStart > 0 && refElement.current.classList.contains('active')) {
      refElement.current.selectionStart = cursorIndex
      refElement.current.selectionEnd = cursorIndex
    }

    // cursorIndex sadly cannot be in the dependency array, because it prevents highlighting a text area
  }, [section?.values])

  // when using jumpCodes, update cursor when we change asterisks
  useEffect(() => {
    if (useJumpCode && isActiveSection) {
      // scroll jumpCode asterisk into view
      let { top: topOfCursor } = getCaretCoordinates(refElement.current, cursorIndex)
      let { top: topOfRef } = refElement.current.getBoundingClientRect()
      const noteHolderDiv = document.getElementsByClassName('note-body')[0]

      // get the starting position of the note on the page, and add 100 for an offset
      const offsetGuess = noteHolderDiv.getBoundingClientRect().top + 100

      noteHolderDiv.scrollTo({
        // from where the note is currently scrolled to, go to the new asterisk x-coord, minus offset
        top: (noteHolderDiv.scrollTop || 0) + topOfRef + topOfCursor - offsetGuess,
        behavior: 'smooth',
      })

      // update cursor to follow the new asterisk, and to highlight it as well
      refElement.current.selectionStart = cursorIndex - asteriskSearchLength
      refElement.current.selectionEnd = cursorIndex
      dispatch(turnOffJumpCode())
    }
  }, [dispatch, useJumpCode, cursorIndex, isActiveSection])

  // When we add lines to the text-area, adjust height accordingly
  const adjustTextAreaHeight = () => {
    if (refElement.current && hiddenRef.current) {
      refElement.current.style.height = hiddenRef.current.scrollHeight + 24 + 'px'
    }
  }

  const resetDotPhrasing = () => {
    setUseDotPhrase(false)
    setStartingDot(-1)
    setDotCaret(0)
  }

  const dotQueryPhrase = valueText.slice(startingDot, dotCaret)
  const filteredSnippets = snippets
    .filter(
      (snip) => snip?.title && snip.title.toLowerCase().startsWith(dotQueryPhrase.toLowerCase()),
    )
    .sort((a, b) => a.title.localeCompare(b.title))

  // if cursor moves to different area, close dotPhrase
  useEffect(() => {
    if (
      (startingDot !== -1 && cursorIndex < startingDot) ||
      (dotCaret !== 0 && cursorIndex > dotCaret)
    ) {
      resetDotPhrasing()
    }
  }, [cursorIndex, startingDot, dotCaret])

  const onChange = (e, action = ACTIONS.TYPE) => {
    if (isDraftSaved) {
      setIsDraftSaved(false)
    }
    adjustTextAreaHeight()

    let caret = getCaretCoordinates(e.target, e.target.selectionEnd)
    setDotPhrasePosition(caret)
    const selectionEnd = e.target.selectionEnd
    const penultimateSelectionChar = e.currentTarget?.value?.[selectionEnd - 2]
    const lastSelectionChar = e.currentTarget?.value?.[selectionEnd - 1]

    if (useDotPhrase) {
      setDotCaret(selectionEnd)
      if (lastSelectionChar === ' ' || lastSelectionChar === '.') {
        resetDotPhrasing()
      }
    } else if (e.nativeEvent.inputType === 'insertText') {
      if (
        (penultimateSelectionChar === ' ' ||
          penultimateSelectionChar === '\n' ||
          penultimateSelectionChar === undefined) &&
        lastSelectionChar === '.'
      ) {
        setStartingDot(selectionEnd)
        setUseDotPhrase(true)
      }
    }

    let newText = e.currentTarget?.value

    // if we are just focus or blur saving, dont dispatch a same change
    if (action === ACTIONS.TYPE || newText !== valueText) {
      dispatch(
        setNoteSectionValue({
          title: section?.title,
          text: e.currentTarget?.value,
        }),
      )
    }
    dispatch(changeCursor(selectionEnd))
  }

  const switchUserToLeftSideOfNote = (e) => {
    if (!isTyping) {
      setIsTyping(true)
    }

    // when dragon is on, clicking around the text area causes
    // the textarea to lose dragon notes, so don't do whatever
    // logic this was in charge of while dragon is on
    if (!dragonMicOn) {
      dispatch(changeCursor(e.target.selectionEnd))
    }

    // WARNING
    // we can't call onChange in here because it breaks
    // dragging and dropping text

    // update activeSection
    if (!activeSection) {
      activeSectionHandle(section)
    }

    //update right side tab if necessary
    if (currentTab === TAB_OPTIONS.SECTION) {
      setCurrentTab(TAB_OPTIONS.TEMPLATE)
    }
  }

  const processDotPhraseInteraction = (e) => {
    // use arrow keys to highlight the different dotPhrase snippets
    // ignore others to prevent unforeseeable errors
    if (e.key === 'ArrowUp') {
      setHighlightedSnippet((prev) => Math.max(0, prev - 1))
      e.preventDefault()
    } else if (e.key === 'ArrowDown') {
      setHighlightedSnippet((prev) => Math.min(filteredSnippets.length - 1, prev + 1))
      e.preventDefault()
    } else if (e.key === 'Enter') {
      dispatch(
        onDotPhraseSnippetAdd({
          snippetText: filteredSnippets[highlightedSnippet]?.snippets,
          startingDot,
        }),
      )
      resetDotPhrasing()
      e.preventDefault()
    } else if (KEYS_DOT_PHRASE_IGNORES.includes(e.key)) {
      e.preventDefault()
    }
  }

  const debounceJumpCodeCommand = _.debounce((e) => {
    dispatch(jumpToAsterisk())
  }, 350)

  return (
    <>
      <div className="section-holder">
        <h6 className="title">
          {section?.title} {!section.optional && '*'}
        </h6>
        <div
          className="section-item-holder note-txt-input"
          style={{ zIndex: isActiveSection ? 10 : 'inherit' }}
        >
          <CFormTextarea
            style={{ visibility: 'hidden', position: 'absolute' }}
            type="textarea"
            ref={hiddenRef}
            readOnly={true}
            value={valueText}
          />
          <CFormTextarea
            data-nusa-enabled="true"
            type="textarea"
            style={{ overflow: 'hidden', padding: '8px' }}
            id={section?.title + ' text area'}
            placeholder="Type here or select template or snippets"
            className={`${isActiveSection ? 'active' : ''}`}
            ref={refElement}
            readOnly={!isActiveSection}
            value={valueText}
            onChange={onChange}
            onClick={switchUserToLeftSideOfNote}
            onFocus={(e) => {
              if (!useJumpCode) {
                // adding to prevent race conditions for regenerating left side values
                setIsTyping(true)

                // test onChange call that seems to work in preventing text from deleting
                // when clicking into a text area repeatedly
                if (dragonMicOn) {
                  onChange(e, ACTIONS.FOCUS)
                }

                // if user wants text cursor to go to end of text area, we could do that here too
                // we would just need to maybe stop propagation so `switchUserToLeftSideOfNote`
                // doesnt trigger as well
                activeSectionHandle(section)

                // save draft too
                onSaveDraft()
              }
            }}
            onKeyDown={(e) => {
              if (useDotPhrase) {
                processDotPhraseInteraction(e)
              } else if ((e.ctrlKey && e.key === 'h') || (e.key === 'Tab' && asterisksFound)) {
                e.preventDefault()
                debounceJumpCodeCommand(e)
              }
            }}
            onBlur={(e) => {
              // call onChange to save dragon text before clicking to another box
              if (dragonMicOn && !useDotPhrase) {
                onChange(e, ACTIONS.BLUR)
              }
            }}
          />

          {useDotPhrase && filteredSnippets?.length > 0 && (
            <DotPhrase
              closeDotPhrase={resetDotPhrasing}
              onSelectSnippet={(snippetText) => {
                dispatch(onDotPhraseSnippetAdd({ snippetText, startingDot }))
                resetDotPhrasing()
              }}
              dotPhrasePosition={dotPhrasePosition}
              snippets={filteredSnippets}
              highlightedSnippet={highlightedSnippet}
              setHighlightedSnippet={setHighlightedSnippet}
            />
          )}
        </div>
        {section?.errors && (
          <div style={{ fontSize: 11, color: 'var(--color-warning)' }}>
            <i className="fa-solid fa-triangle-exclamation icon" style={{ fontSize: '1.4em' }}></i>
            &nbsp;
            {section?.errors}
          </div>
        )}
      </div>
    </>
  )
}

export default DynamicSection
